/* eslint-disable no-unused-vars */
import moment from 'moment';
import { store } from 'redux/store';

import http from './http.service';
import Promisable from './promisable.service';
import { cacheActions } from '@redux/slices/cache';
import { getAppDispatch } from '@utilities/dispatch.util';
import { jurisdictionNumbersActions } from '@redux/slices/jurisdiction-numbers';
import { pvPsvActions } from '@redux/slices/pv-psv';
import { globalProgressBarAction } from '@redux/slices/global-progress-bar';
import { initWebWorker } from 'webworker';
import LocalStorage from '@utilities/local-storage.util';
import _ from 'lodash';
import { decodeToken } from '@utilities/authentication';
import { revertEditEquipmentData } from '@components/add-edit-equipment/helper';
import {
  equipmentDispatcher,
  equipmentJsonManipulationJson,
  newAndEditedSyncedEquipmentManipulationJson
} from './cache-services/json-manipulation/equipment';

const EquipmentService = {
  getJurisdictionNumbers: async () => {
    const dispatch = getAppDispatch();
    const cachingFailure = localStorage.getItem('cachingFailure');

    if (!JSON.parse(cachingFailure)) {
      dispatch?.(jurisdictionNumbersActions.setLoading(true));

      http.setJWT();
      const [success, error] = await Promisable.asPromise(http.get(`/JurisdictionNumbers?page=0`));

      if (success)
        dispatch?.(jurisdictionNumbersActions.setJurisdictionNumbers(success.data.items));

      dispatch?.(jurisdictionNumbersActions.setLoading(false));
      dispatch?.(globalProgressBarAction.setLoadStatus(7));

      return [success, error];
    } else throw new Error('Api failed to update');
  },

  getPVPSV: async () => {
    const dispatch = getAppDispatch();
    dispatch?.(pvPsvActions.setLoading(true));

    const cachingFailure = localStorage.getItem('cachingFailure');

    if (!JSON.parse(cachingFailure)) {
      http.setJWT();
      const [success, error] = await Promisable.asPromise(http.get(`/PVPSV/mobile?page=0`));

      if (success) dispatch?.(pvPsvActions.setPVPSV(success.data.items));

      dispatch?.(pvPsvActions.setLoading(false));
      dispatch?.(globalProgressBarAction.setLoadStatus(6));

      return [success, error];
    } else throw new Error('Api failed to update');
  },
  setEquipments: async ({ initialLogin, lastSuccessFullRefreshTime, isSettingUpdated }) => {
    const { cache } = store.getState();
    const { equipmentsMap = {}, equipments = [] } = cache;
    const cachingFailure = localStorage.getItem('cachingFailure');

    if (!JSON.parse(cachingFailure)) {
      const changedAfter = lastSuccessFullRefreshTime;
      const dispatch = getAppDispatch();
      http.setJWT();
      const [success, error] = await Promisable.asPromise(
        http.get(
          `/Equipment/mobile?page=1&changedAfter=${
            !initialLogin
              ? changedAfter
                ? equipments?.length === 0
                  ? ''
                  : changedAfter
                : ''
              : ''
          }`
        )
      );

      const isCheckOldAndNewArray =
        !initialLogin && success?.data?.items?.length && equipments?.length && !isSettingUpdated;

      const data = await initWebWorker({
        args: {
          equipmentPayloadData: !isCheckOldAndNewArray ? success?.data?.items || [] : [],
          isCheckOldAndNewArray: isCheckOldAndNewArray
            ? {
                oldArray: equipments,
                oldArrayMap: equipmentsMap,
                newArray: success?.data?.items,
                idName: 'equipmentId'
              }
            : false
        },
        type: 'equipment-mobile-data-redux',
        timeout: 1000000
      });

      dispatch?.(cacheActions.setEquipments(data?.result));

      dispatch?.(globalProgressBarAction.setLoadStatus(4));
      return [success, error];
    } else throw new Error('Api failed to update');
  },
  geEquipmentsFromStorage: async ({ userId }) => {
    try {
      const dispatch = getAppDispatch();

      let pendingOrInProgressAssets = [];
      let editOrNewAssetJurisdictionNumbers = [];
      let newPsvsList = [];

      const pendingSyncAndInProgressTasksOfCurrentUser = (
        (await LocalStorage.getItem(
          `userPendingSyncAndInProgressAssets-${decodeToken().userId}`
        )) || []
      )?.find((item) => item.userId === userId);

      // console.log({ pendingSyncAndInProgressTasksOfCurrentUser });

      if (!_.isEmpty(pendingSyncAndInProgressTasksOfCurrentUser)) {
        pendingOrInProgressAssets = !_.isEmpty(pendingSyncAndInProgressTasksOfCurrentUser)
          ? pendingSyncAndInProgressTasksOfCurrentUser?.assets
          : [];

        editOrNewAssetJurisdictionNumbers = !_.isEmpty(pendingSyncAndInProgressTasksOfCurrentUser)
          ? pendingSyncAndInProgressTasksOfCurrentUser?.jurisdictionNumbers
          : [];

        newPsvsList = !_.isEmpty(pendingSyncAndInProgressTasksOfCurrentUser)
          ? pendingSyncAndInProgressTasksOfCurrentUser?.newPsvsList
          : [];
      }

      // console.log({ pendingOrInProgressAssets, editOrNewAssetJurisdictionNumbers });

      dispatch?.(cacheActions.setEquipments1(pendingOrInProgressAssets || []));
      dispatch?.(
        jurisdictionNumbersActions.setJurisdictionNumbers1(editOrNewAssetJurisdictionNumbers || [])
      );

      dispatch?.(pvPsvActions.setNewPSVFromStorage({ newPsvsList }));

      await LocalStorage.removeItem(`userPendingSyncAndInProgressAssets-${decodeToken().userId}`);
    } catch (error) {
      console.log({ error });
    }
  },
  deleteNewOrExistingEquipment: async ({ equipmentId, siteId }) => {
    const dispatch = getAppDispatch();
    dispatch?.(cacheActions.deleteEquipment({ equipmentId, siteId }));
    console.log({ equipmentId, siteId });

    // const userId = store.getState().auth.userId;
    // await deleteTaskFromLocalStorageAfterSuccessFullSynced({ userId, inspections: true, id });
  },
  resetExistingOrExistingEquipment: ({ id }) => {
    const dispatch = getAppDispatch();

    const { transform, transformJurisdiction } = revertEditEquipmentData({
      equipmentId: id
    });

    let transformCopy = _.cloneDeep(transform);

    dispatch?.(
      cacheActions.updateExistingEquipments({
        ...transformCopy,
        equipmentId: id,
        equipmentEditingStatus: '',
        //  vesselEquipmentId && equipmentDetails?.equipmentEditingStatus
        //    ? equipmentDetails?.equipmentEditingStatus
        //    : '',
        verificationStatus:
          //  equipmentDetails?.verificationStatus ||
          ''
      })
    );

    if (transformJurisdiction)
      dispatch?.(
        jurisdictionNumbersActions.updateEquipmentJurisdictionNumbers({
          ...transformJurisdiction
        })
      );
  },
  getAllNewAndUpdateSyncedEquipment: async ({ initialLogin, isSettingUpdated }) => {
    const dispatch = getAppDispatch();
    const { cache } = store.getState();
    const { syncedNewAndEditedEquipments = [], lastSuccessFullRefreshTime: changedAfter } = cache;

    const {
      jurisdictionNumbers: oldJurisdictionNumbers,
      jurisdictionNumbersMap: oldJurisdictionNumbersMap
    } = store.getState()?.jurisdictionNumbers || {};

    const cachingFailure = localStorage.getItem('cachingFailure');

    if (!JSON.parse(cachingFailure)) {
      http.setJWT();
      const res = await Promise.all([
        Promisable.asPromise(
          http.get(
            `/ReconciliationRecords/mobilecache?changedAfter=${
              !initialLogin
                ? changedAfter
                  ? syncedNewAndEditedEquipments?.length === 0
                    ? ''
                    : changedAfter
                  : ''
                : ''
            }`
          )
        ),
        Promisable.asPromise(http.get(`/JurisdictionNumbers?page=0`)),
        Promisable.asPromise(http.get(`/Equipment/mobile?page=0`))
      ]);

      let [
        [syncedEquipments = []] = [],
        [jurisdictionNumbers = []] = [],
        [equipmentList = []] = []
      ] = res;
      syncedEquipments = syncedEquipments?.data || [];
      jurisdictionNumbers = jurisdictionNumbers?.data?.items || [];
      equipmentList = equipmentList?.data || [];

      let newSyncedEquipments = await newAndEditedSyncedEquipmentManipulationJson({
        initialLogin,
        data: syncedEquipments,
        isSettingUpdated
      });

      const isCheckOldAndNewArray =
        !initialLogin &&
        jurisdictionNumbers?.length &&
        oldJurisdictionNumbers?.length &&
        !isSettingUpdated;

      let newList = [];

      newList = !isCheckOldAndNewArray
        ? jurisdictionNumbers?.map((task) => {
            const found =
              oldJurisdictionNumbers.findIndex((t) => t.equipmentId === task.equipmentId) + 1;

            if (found && oldJurisdictionNumbers[found - 1].isEdit) {
              return { ...oldJurisdictionNumbers[found - 1] };
            } else {
              return task;
            }
          })
        : jurisdictionNumbers;

      let newNumber = oldJurisdictionNumbers?.filter((x) => x?.isNew) || [];

      newList = [...newList, ...newNumber];

      const newJurisdictionNumbers = newList || [];
      const jurisdictionNumbersMap = jurisdictionNumbers.reduce((acc, curr) => {
        acc[curr.equipmentId] = curr;
        return acc;
      }, {});

      dispatch?.(cacheActions.setSyncedEquipments(newSyncedEquipments));
      dispatch?.(
        jurisdictionNumbersActions.setJurisdictionNumbers({
          jurisdictionNumbers: newJurisdictionNumbers,
          jurisdictionNumbersMap
        })
      );

      const result = await equipmentJsonManipulationJson({
        initialLogin,
        data: equipmentList,
        isSettingUpdated
      });
      equipmentDispatcher({ data: result });
    } else throw new Error('Api failed to update');
  }
};

export default EquipmentService;
